import type { AIDifficulty, PlayerId } from '@/types';

const STORAGE_KEY = 'miniwarriors_ratings';
const DEFAULT_RATING = 1000;
const MIN_RATING = 100;
const K_FACTOR = 32;

export interface PlayerRatings {
  player1: number;
  player2: number;
}

export interface RatingChange {
  winnerDelta: number;
  loserDelta: number;
}

export interface TieRatingChange {
  player1Delta: number;
  player2Delta: number;
}

/**
 * Service for managing player ELO ratings
 */
export const RatingService = {
  /**
   * Calculate ELO rating changes after a match
   */
  calculateEloChange(winnerRating: number, loserRating: number): RatingChange {
    // Expected score for winner
    const expectedWinner = 1 / (1 + Math.pow(10, (loserRating - winnerRating) / 400));
    // Expected score for loser
    const expectedLoser = 1 / (1 + Math.pow(10, (winnerRating - loserRating) / 400));

    // Calculate deltas (winner gets 1, loser gets 0)
    const winnerDelta = Math.round(K_FACTOR * (1 - expectedWinner));
    const loserDelta = Math.round(K_FACTOR * (0 - expectedLoser));

    return { winnerDelta, loserDelta };
  },

  /**
   * Calculate ELO rating changes for a tie
   * In ELO, a tie is worth 0.5 points for each player
   */
  calculateTieChange(player1Rating: number, player2Rating: number): TieRatingChange {
    // Expected score for player1
    const expectedP1 = 1 / (1 + Math.pow(10, (player2Rating - player1Rating) / 400));
    // Expected score for player2
    const expectedP2 = 1 / (1 + Math.pow(10, (player1Rating - player2Rating) / 400));

    // Calculate deltas (both get 0.5)
    const player1Delta = Math.round(K_FACTOR * (0.5 - expectedP1));
    const player2Delta = Math.round(K_FACTOR * (0.5 - expectedP2));

    return { player1Delta, player2Delta };
  },

  /**
   * Get AI rating based on difficulty level
   */
  getAIRating(difficulty: AIDifficulty): number {
    switch (difficulty) {
      case 'easy':
        return 800;
      case 'medium':
        return 1000;
      case 'hard':
        return 1200;
    }
  },

  /**
   * Load ratings from localStorage
   */
  loadRatings(): PlayerRatings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as PlayerRatings;
        return {
          player1: Math.max(MIN_RATING, parsed.player1 ?? DEFAULT_RATING),
          player2: Math.max(MIN_RATING, parsed.player2 ?? DEFAULT_RATING),
        };
      }
    } catch {
      // Ignore parse errors
    }
    return { player1: DEFAULT_RATING, player2: DEFAULT_RATING };
  },

  /**
   * Save ratings to localStorage
   */
  saveRatings(ratings: PlayerRatings): void {
    try {
      const clamped: PlayerRatings = {
        player1: Math.max(MIN_RATING, ratings.player1),
        player2: Math.max(MIN_RATING, ratings.player2),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(clamped));
    } catch {
      // Ignore storage errors (e.g., private browsing)
    }
  },

  /**
   * Reset ratings to default
   */
  resetRatings(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  },

  /**
   * Apply rating changes and return new ratings
   */
  applyRatingChange(
    ratings: PlayerRatings,
    winner: PlayerId,
    change: RatingChange
  ): PlayerRatings {
    const newRatings = { ...ratings };

    if (winner === 'player1') {
      newRatings.player1 = Math.max(MIN_RATING, ratings.player1 + change.winnerDelta);
      newRatings.player2 = Math.max(MIN_RATING, ratings.player2 + change.loserDelta);
    } else {
      newRatings.player2 = Math.max(MIN_RATING, ratings.player2 + change.winnerDelta);
      newRatings.player1 = Math.max(MIN_RATING, ratings.player1 + change.loserDelta);
    }

    return newRatings;
  },

  /**
   * Get default rating value
   */
  getDefaultRating(): number {
    return DEFAULT_RATING;
  },
};
